import { useEffect, useRef, useState, type ComponentProps } from 'react'
import { Alert, Badge, Button, Skeleton, TextLink } from '@indipia/ui-core'
import { COMM_METHOD_DIRECT_LABEL, isDirectRecord, type SignalItem } from '@indipia/shared'
import type { OrderVendorRow } from '../../api/orderApi'
import { useMaterialOrderData, useOrderActions, useVendorDirectory } from '../../hooks/useMaterialOrders'
import { useTaskSignals } from '../../hooks/useTaskSignals'
import { useSchedulerHost } from '../../context/SchedulerHostContext'
import { slotItem } from '../../constants/signalView'
import {
  canRelease, getMaterialProgressSteps, getSendAction, isDirectOrderTask, isMaterialStepKey, leadTimeDays, leadTimeText,
  MAX_VENDORS_PER_TASK, ORDER_MESSAGE, ORDER_STATUS_LABEL, orderErrorMessage, type MaterialStepKey,
} from '../../constants/materialOrder.logic'
import { formatMonthDay } from '../PartnerCandidateItem'
import { ErrorBanner } from '../AsyncState'
import { ConfirmProgressSummary } from '../ConfirmProgressSummary'
import { TaskDetailCard } from '../TaskDetailCard'
import { PhoneIcon } from '../PhoneIcon'
import { EditPencilIcon } from './EditPencilIcon'
import { MaterialEvidenceModal } from './MaterialEvidenceModal'
import { MaterialStepDetailModal } from './MaterialStepDetailModal'
import { OrderDateEditor } from './OrderDateEditor'
import { OrderReplyResult } from './OrderReplyResult'
import { OrderStatusLine } from './OrderStatusLine'
import { VendorPicker } from './VendorPicker'

// 간트 공종 상세창 "자재파트너" 카드 (구현지시서 §10, 940 시트1 R41·R53).
// 데이터·신호는 팝업과 같은 캐시(useMaterialOrderData·useTaskSignals)를 공유한다.
// 거래처 변경·날짜 수정은 이 카드에서 즉시 저장하고, 발송 동작은 모두 C-3 팝업을 열어 처리한다(발송 구현은 팝업 한 곳).
// 한 공종에 자재파트너는 최대 5곳(B-12) — 거래처별 줄로 나눠 줄마다 거래처 선택·상태·날짜·진행 요약을 둔다.
// 카드 맨 위 신호 한 줄은 서버가 계산한 ORDER 신호(여러 곳 중 가장 나쁜 상태 하나)다.
// 날짜(발주일·입고희망일)는 공종 단위 값이라 어느 줄에서 고쳐도 모든 줄에 같이 반영된다.

export interface MaterialPartnerCardChange {
  taskId: number
  vendorId: number | null
  vendorName: string
  orderDate: string | null
  deliveryDate: string | null
}

export interface MaterialPartnerCardTask {
  taskId: number
  workTypeCode: string
  /** YYYY-MM-DD */
  startDate: string
  endDate: string
}

interface Props {
  task: MaterialPartnerCardTask
  siteId: number
  scheduleId: number
  /** 딥링크 card=sup — 값이 있으면 진행 요약을 펼치고 카드를 화면에 보인다(같은 공종에 다시 들어오면 값이 바뀜). 없으면 undefined */
  expandNonce?: number
  onOpenOrderPopup: (taskId: number) => void
  onChanged?: (change: MaterialPartnerCardChange) => void
}

type SignalProp = ComponentProps<typeof OrderStatusLine>['signal']

const label12: React.CSSProperties = { fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }

/** 보낸 발주정보 요약 한 줄 — 값이 없는 조각은 생략(채우지 않음) */
function summarizeOrder(row: OrderVendorRow, deliveryDate: string | null): string {
  if (row.items.length === 0) return ORDER_MESSAGE.card.noItems
  const first = row.items[0].itemName
  const parts = [row.items.length > 1 ? `${first} 외 ${row.items.length - 1}건` : first]
  if (row.liftReqYn === 'Y') parts.push(ORDER_MESSAGE.card.liftYes)
  else if (row.liftReqYn === 'N') parts.push(ORDER_MESSAGE.card.liftNo)
  const md = formatMonthDay(deliveryDate)
  if (md) parts.push(`입고 ${md}`)
  return parts.join(' · ')
}

/** 날짜 글자 `발주 M/D → 입고 M/D · 리드타임 N일 · 착공 N일 전 발주`(N = 착공 − 발주) */
function datesText(orderDate: string | null, deliveryDate: string | null, startDate: string): string {
  const hasDates = orderDate != null && deliveryDate != null
  if (!hasDates) return ORDER_MESSAGE.card.datesUnset
  const lead = leadTimeDays(orderDate, startDate)
  return `발주 ${formatMonthDay(orderDate)} → 입고 ${formatMonthDay(deliveryDate)}${lead != null ? ` · ${leadTimeText(lead)}` : ''}`
}

export function MaterialPartnerCard({ task, siteId, scheduleId, expandNonce, onOpenOrderPopup, onChanged }: Props) {
  const { taskId } = task
  const data = useMaterialOrderData(scheduleId)
  const signalsQ = useTaskSignals(siteId)

  const [addingVendor, setAddingVendor] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // 딥링크 card=sup 로 들어오면 호출 화면이 이 공종에 대한 요청 값을 넘긴다(없으면 undefined) — 값이 있으면 진행 요약을 펼친다.
  // 호출 화면이 공종 전환·닫기 때 값을 거두므로 카드는 값의 유무만 본다.
  const expanded = expandNonce !== undefined
  useEffect(() => {
    setAddingVendor(false)
  }, [taskId])
  useEffect(() => {
    if (expanded) rootRef.current?.scrollIntoView({ block: 'nearest' })
  }, [expanded, expandNonce])

  // ── 조회 상태 ──
  if (data.status === 'loading') {
    return (
      <TaskDetailCard title={ORDER_MESSAGE.card.title} emphasis>
        <div aria-busy="true" className="flex flex-col gap-1.5">
          {[0, 1, 2].map(i => <Skeleton key={i} height={24} />)}
        </div>
      </TaskDetailCard>
    )
  }
  if (data.status === 'error') {
    return (
      <TaskDetailCard title={ORDER_MESSAGE.card.title} emphasis>
        <ErrorBanner message={ORDER_MESSAGE.card.loadError} onRetry={data.reload} />
      </TaskDetailCard>
    )
  }

  const taskOrder = data.tasks.find(t => t.taskId === taskId) ?? null
  if (!taskOrder) {
    // 서버가 이 공종을 주지 않음 — 값을 가정하지 않고 오류 표시
    return (
      <TaskDetailCard title={ORDER_MESSAGE.card.title} emphasis>
        <ErrorBanner message={ORDER_MESSAGE.card.loadError} onRetry={data.reload} />
      </TaskDetailCard>
    )
  }

  // 상세창 '이 현장만' 방식(orderModeOverride)이 있으면 회사 설정보다 우선.
  // 발주가 없는 공종은 카드를 그리지 않는다 — 상세창 맨 위 발주·시공 방식 카드가 '시공파트너가 자재까지 가져와 시공'을 이미 보여준다
  if (!isDirectOrderTask(data.settings, taskOrder)) return null

  // 이 공종의 활성 거래처 줄(서버 순서 그대로, 교체돼 닫힌 줄 제외)
  const rows: OrderVendorRow[] = data.rows.filter(r => r.taskId === taskId && r.orderStatusCode !== 'REPLACED')

  // 서버 ORDER 신호 — 폴백 없음(조회 오류면 오류 표시, 이 공종 신호가 없으면 상태 줄을 그리지 않는다). 여러 곳이면 서버가 가장 나쁜 상태 하나를 준다.
  const orderItem: SignalItem | undefined = slotItem(signalsQ.data?.find(s => s.taskId === taskId), 'ORDER')
  const signal = (
    signalsQ.isError ? { state: 'error' }
      : signalsQ.isLoading ? { state: 'loading' }
        : orderItem ? { state: 'ok', item: orderItem }
          : null
  ) as SignalProp | null
  const primaryName = rows[0]?.vendorName ?? ''
  const taskCtx = { startDate: task.startDate, endDate: task.endDate, vendorName: primaryName || null }

  const canAdd = rows.length > 0 && rows.length < MAX_VENDORS_PER_TASK
  const vendorIdsOf = (list: OrderVendorRow[]) => list.map(r => r.vendorId)

  return (
    <div ref={rootRef}>
      <TaskDetailCard
        title={ORDER_MESSAGE.card.title}
        emphasis
        tag={
          rows.length > 1
            ? <Badge status="mute">{ORDER_MESSAGE.card.vendorCount(rows.length)}</Badge>
            : rows.length === 0 ? <Badge status="mute">{ORDER_MESSAGE.card.tagNone}</Badge> : undefined
        }
      >
        {/* 카드 맨 위 상태 한 줄 — 서버 ORDER 신호(가장 나쁜 상태) */}
        {signal && (
          <div aria-live="polite" className="mb-02">
            <OrderStatusLine signal={signal} taskCtx={taskCtx} />
          </div>
        )}

        {rows.length === 0 ? (
          <VendorLine
            key="empty"
            row={null}
            rows={rows}
            task={task}
            scheduleId={scheduleId}
            siteId={siteId}
            taskWorkTypeName={taskOrder.workTypeName}
            fallbackDates={{ orderDate: taskOrder.orderDate, deliveryDate: taskOrder.deliveryDate, confirmed: taskOrder.deliveryConfirmedYn === 'Y' }}
            expanded={expanded}
            expandNonce={expandNonce}
            onOpenOrderPopup={onOpenOrderPopup}
            onChanged={onChanged}
          />
        ) : rows.map((row, i) => (
          <VendorLine
            key={row.orderVendorId}
            row={row}
            rows={rows}
            task={task}
            scheduleId={scheduleId}
            siteId={siteId}
            taskWorkTypeName={taskOrder.workTypeName}
            separated={i > 0}
            expanded={expanded}
            expandNonce={expandNonce}
            onOpenOrderPopup={onOpenOrderPopup}
            onChanged={onChanged}
          />
        ))}

        {/* 거래처 추가 지정 — 최대 5곳 */}
        {canAdd && (
          <div className="mt-2">
            {addingVendor ? (
              <AddVendorLine
                taskId={taskId}
                scheduleId={scheduleId}
                siteId={siteId}
                rows={rows}
                excludeVendorIds={vendorIdsOf(rows)}
                onDone={() => setAddingVendor(false)}
                onChanged={onChanged}
              />
            ) : (
              <TextLink onMouseDown={e => e.stopPropagation()} onClick={() => setAddingVendor(true)}>{`+ ${ORDER_MESSAGE.card.addVendor}`}</TextLink>
            )}
          </div>
        )}
        {rows.length >= MAX_VENDORS_PER_TASK && <div className="mt-2" style={label12}>{ORDER_MESSAGE.card.vendorMax}</div>}
      </TaskDetailCard>
    </div>
  )
}

// ── 거래처 줄 ────────────────────────────────────────────────────────────

interface LineProps {
  /** 이 줄의 발주 거래처 행. 행 없음(미배정)이면 null */
  row: OrderVendorRow | null
  /** 이 공종의 활성 거래처 행 전체(다른 줄 지정 거래처 제외·알림 계산용) */
  rows: OrderVendorRow[]
  task: MaterialPartnerCardTask
  scheduleId: number
  siteId: number
  taskWorkTypeName: string
  /** 행이 없을 때 쓰는 공종 단위 날짜 */
  fallbackDates?: { orderDate: string | null; deliveryDate: string | null; confirmed: boolean }
  /** 둘째 줄부터 위 구분선 */
  separated?: boolean
  expanded: boolean
  expandNonce?: number
  onOpenOrderPopup: (taskId: number) => void
  onChanged?: (change: MaterialPartnerCardChange) => void
}

function VendorLine({
  row, rows, task, scheduleId, siteId, taskWorkTypeName, fallbackDates, separated, expanded, expandNonce, onOpenOrderPopup, onChanged,
}: LineProps) {
  const { taskId } = task
  const host = useSchedulerHost()
  const directory = useVendorDirectory()
  const actions = useOrderActions({ scheduleId, siteId })

  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const [editingDates, setEditingDates] = useState(false)
  const [pickError, setPickError] = useState('')
  const [stepKey, setStepKey] = useState<MaterialStepKey | null>(null)

  useEffect(() => {
    setEvidenceOpen(false)
    setEditingDates(false)
    setPickError('')
    setStepKey(null)
  }, [taskId])

  const orderDate = row ? row.orderDate : fallbackDates?.orderDate ?? null
  const deliveryDate = row ? row.deliveryDate : fallbackDates?.deliveryDate ?? null
  const confirmed = row ? row.deliveryConfirmedYn === 'Y' : fallbackDates?.confirmed ?? false
  const vendorName = row?.vendorName ?? ''
  const phone = row ? directory.byId.get(row.vendorId)?.vendorPhone ?? null : null
  // 결과만 직접등록 배지(SIF-3) — 고른 거래처의 연락 방식(파트너 등록값)
  const directRecord = row ? isDirectRecord(directory.byId.get(row.vendorId)?.commYn) : false

  /** 호출 화면(간트)에는 첫 줄 거래처를 이 공종의 대표 거래처로 알린다 */
  const notifyRows = (next: OrderVendorRow[], dates: { orderDate: string | null; deliveryDate: string | null }) => {
    const primary = next[0] ?? null
    onChanged?.({
      taskId,
      vendorId: primary?.vendorId ?? null,
      vendorName: primary?.vendorName ?? '',
      orderDate: dates.orderDate,
      deliveryDate: dates.deliveryDate,
    })
  }

  const handlePick = async (vendorId: number) => {
    setPickError('')
    try {
      const next = row
        ? (await actions.replace(row.orderVendorId, vendorId)).created
        : await actions.assign(taskId, vendorId)
      notifyRows(row ? rows.map(r => (r.orderVendorId === row.orderVendorId ? next : r)) : [next], next)
    } catch (err) {
      setPickError(orderErrorMessage(err, { vendorName: '' }))
    }
  }
  const handleRelease = async () => {
    if (!row) return
    setPickError('')
    try {
      await actions.release(row.orderVendorId, taskId)
      notifyRows(rows.filter(r => r.orderVendorId !== row.orderVendorId), { orderDate, deliveryDate })
    } catch (err) {
      setPickError(orderErrorMessage(err))
    }
  }
  const handleDates = async (patch: { orderDate?: string | null; deliveryDate?: string | null }) => {
    const res = await actions.changeDates(taskId, patch)
    notifyRows(rows, res)
    return res
  }

  // ── 태그 ──
  const tag = !row
    ? null   // 미배정 태그는 카드 제목 옆에 둔다
    : row.assignMethodCode === 'AUTO'
      ? (
        <TextLink title={ORDER_MESSAGE.card.tagAutoLabel} aria-label={ORDER_MESSAGE.card.tagAutoLabel} onMouseDown={e => e.stopPropagation()} onClick={() => setEvidenceOpen(true)}>
          <Badge status="info">{ORDER_MESSAGE.card.tagAuto}</Badge>
        </TextLink>
      )
      : row.assignMethodCode === 'MANUAL'
        ? <Badge status="mute">{ORDER_MESSAGE.card.tagManual}</Badge>
        : null   // 이행 행은 배정 방식이 없음 — 배지 없음
  // 줄 상태(서버 발주 상태 이름) — 신호를 대신하지 않는 보조 표기
  const statusLabel = row ? (ORDER_STATUS_LABEL as Record<string, string | undefined>)[row.orderStatusCode] : undefined

  // ── 날짜 ──
  const hasDates = orderDate != null && deliveryDate != null
  const dateText = datesText(orderDate, deliveryDate, task.startDate)

  // ── 발송·불가 회신 버튼(모두 팝업을 연다) ──
  // 결과만 직접등록(SIF-3 C-1) — [발주정보 보내기]·[다시 보내기] 자리에 [발주 기록](같은 발주정보발송 팝업을 연다)
  const sendLabel = row ? getSendAction(row, directRecord).label : undefined
  const declined = row?.orderStatusCode === 'DECLINED' && row.lastReply?.replyResultCode === 'DECLINED'
  const availMd = declined ? formatMonthDay(row?.lastReply?.availDate) : ''
  const openPopup = () => onOpenOrderPopup(taskId)
  const steps = getMaterialProgressSteps(row, { orderDate, direct: directRecord })
  // 다른 줄에 이미 지정된 거래처는 후보에서 뺀다
  const excludeVendorIds = rows.filter(r => r.orderVendorId !== row?.orderVendorId).map(r => r.vendorId)

  return (
    <div
      className={separated ? 'mt-2 pt-2' : undefined}
      style={separated ? { borderTop: 'var(--border-width-hairline) solid var(--shell-border)' } : undefined}
    >
      {/* 줄 머리 — 배정 태그 · 상태 이름 */}
      {(tag || statusLabel) && (
        <div className="mb-1 flex items-center gap-1.5">
          {tag}
          {statusLabel && <Badge status="mute">{statusLabel}</Badge>}
        </div>
      )}

      {/* 거래처 변경 — 변경 즉시 저장 · 결과만 직접등록 배지 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <VendorPicker
            row={row}
            taskId={taskId}
            excludeVendorIds={excludeVendorIds}
            onPick={vendorId => { void handlePick(vendorId) }}
            onRelease={row && canRelease(row) ? () => { void handleRelease() } : undefined}
          />
        </div>
        {directRecord && <Badge status="warning">{COMM_METHOD_DIRECT_LABEL}</Badge>}
      </div>
      {pickError && <Alert kind="error" className="mt-1">{pickError}</Alert>}

      {/* 전화·이력평가 */}
      <div style={{ marginTop: 5, paddingLeft: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
        {row ? (
          phone ? (
            <>
              <PhoneIcon />
              <span style={label12}>{phone}</span>
            </>
          ) : null
        ) : <span style={{ ...label12, color: 'var(--shell-hint)' }}>{ORDER_MESSAGE.card.noVendor}</span>}
        {row && host?.openVendorHistory && (
          <button
            type="button"
            onMouseDown={e => e.stopPropagation()}
            onClick={() => host.openVendorHistory?.({ vendorId: row.vendorId, vendorName: row.vendorName ?? '' })}
            style={{ marginLeft: 'auto', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: 'var(--shell-on-surface-2)', textDecoration: 'underline', flexShrink: 0 }}
          >{ORDER_MESSAGE.card.history}</button>
        )}
      </div>

      {/* 날짜 글자 + 수정 아이콘 — 기본값을 계산해 채우지 않음 */}
      <div className="mt-2">
        {editingDates ? (
          <OrderDateEditor
            compact
            taskId={taskId}
            orderDate={orderDate}
            deliveryDate={deliveryDate}
            startDate={task.startDate}
            startEditing
            onDone={() => setEditingDates(false)}
            disabled={row?.orderStatusCode === 'SHIP_REPLIED' || row?.orderStatusCode === 'RECEIVED'}
            onChange={handleDates}
          />
        ) : (
          <div className="flex min-w-0 items-center gap-02">
            <span style={{ fontSize: 'var(--type-caption-size)', color: hasDates ? 'var(--shell-on-surface)' : 'var(--shell-hint)', minWidth: 0 }}>{dateText}</span>
            {confirmed && <Badge status="success">{ORDER_MESSAGE.card.confirmed}</Badge>}
            <TextLink aria-label={ORDER_MESSAGE.card.datesEdit} title={ORDER_MESSAGE.card.datesEdit} onMouseDown={e => e.stopPropagation()} onClick={() => setEditingDates(true)}><EditPencilIcon /></TextLink>
          </div>
        )}
      </div>

      {/* 보낸 발주정보 요약 + 안내 */}
      {row && (
        <div className="mt-1.5">
          <div style={label12}><span style={{ fontWeight: 600 }}>{ORDER_MESSAGE.card.orderSummary}</span> : {summarizeOrder(row, deliveryDate)}</div>
          {row.orderStatusCode === 'RESEND_NEEDED' && (
            <div className="mt-1" style={{ ...label12, color: 'var(--status-warning-700)' }}>{ORDER_MESSAGE.card.resendNeeded}</div>
          )}
        </div>
      )}

      {/* 거래처 답장 */}
      {row?.lastReply && <div className="mt-1.5"><OrderReplyResult row={row} /></div>}

      {/* 실행 버튼 — 모두 C-3 팝업을 이 공종 선택 상태로 연다. 시공파트너 카드와 같이 진행 요약 위(발주 내용 아래)에 둔다 */}
      {(sendLabel || declined) && (
        <div className="mt-2 flex flex-col gap-1.5">
          {sendLabel && (
            <Button variant="secondary" size="sm" className="w-full" onMouseDown={e => e.stopPropagation()} onClick={openPopup}>{sendLabel}</Button>
          )}
          {declined && availMd && (
            <Button variant="secondary" size="sm" className="w-full" onMouseDown={e => e.stopPropagation()} onClick={openPopup}>{`${availMd} 입고로 다시 보내기`}</Button>
          )}
          {declined && (
            <Button variant="secondary" size="sm" className="w-full" onMouseDown={e => e.stopPropagation()} onClick={openPopup}>{ORDER_MESSAGE.card.pickOther}</Button>
          )}
        </div>
      )}

      {/* 진행 요약 5단계 — 내역이 있는 단계 줄을 누르면 세부 내역 팝업 */}
      <div className="mt-2">
        <ConfirmProgressSummary
          key={`${row?.orderVendorId ?? 'none'}-${expandNonce ?? 'closed'}`}
          steps={steps}
          defaultOpen={expanded}
          onOpenDetail={row ? key => { if (isMaterialStepKey(key)) setStepKey(key) } : undefined}
        />
      </div>

      {row && row.assignMethodCode === 'AUTO' && (
        <MaterialEvidenceModal open={evidenceOpen} onClose={() => setEvidenceOpen(false)} taskId={taskId} row={row} vendorName={vendorName} workTypeName={taskWorkTypeName} />
      )}
      {row && stepKey && (
        <MaterialStepDetailModal open onClose={() => setStepKey(null)} row={row} stepKey={stepKey} workTypeName={taskWorkTypeName} />
      )}
    </div>
  )
}

// ── 거래처 추가 줄 ───────────────────────────────────────────────────────

interface AddProps {
  taskId: number
  scheduleId: number
  siteId: number
  rows: OrderVendorRow[]
  excludeVendorIds: number[]
  onDone: () => void
  onChanged?: (change: MaterialPartnerCardChange) => void
}

/** 같은 공종에 자재파트너를 한 곳 더 지정한다(A3). 이미 지정된 거래처는 후보에서 뺀다. 한도(5곳)·중복은 서버도 거절한다. */
function AddVendorLine({ taskId, scheduleId, siteId, rows, excludeVendorIds, onDone, onChanged }: AddProps) {
  const actions = useOrderActions({ scheduleId, siteId })
  const [error, setError] = useState('')

  const handlePick = async (vendorId: number) => {
    setError('')
    try {
      const created = await actions.assign(taskId, vendorId)
      const primary = rows[0] ?? created
      onChanged?.({
        taskId, vendorId: primary.vendorId, vendorName: primary.vendorName ?? '',
        orderDate: created.orderDate, deliveryDate: created.deliveryDate,
      })
      onDone()
    } catch (err) {
      setError(orderErrorMessage(err))
    }
  }

  return (
    <div>
      <VendorPicker row={null} taskId={taskId} excludeVendorIds={excludeVendorIds} onPick={vendorId => { void handlePick(vendorId) }} />
      {error && <Alert kind="error" className="mt-1">{error}</Alert>}
      <div className="mt-1">
        <TextLink onMouseDown={e => e.stopPropagation()} onClick={onDone}>{ORDER_MESSAGE.card.addVendorCancel}</TextLink>
      </div>
    </div>
  )
}
